import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  AlertCircle, 
  ChevronRight, 
  ChevronDown,
  AlertTriangle,
  Info,
  XCircle
} from 'lucide-react';
import type { Anomaly, AnomalySeverity } from '@/types/anomaly';
import { consolidateAnomalies, shouldConsolidate } from '@/services/anomalyConsolidationService';
import type { ConsolidatedAnomaly } from '@/services/anomalyConsolidationService';

interface DetectedIssuesPanelProps {
  anomalies: Anomaly[];
  defaultExpanded?: boolean;
  onExpansionChange?: (isExpanded: boolean) => void;
}

interface ExpansionState {
  // Level 1: Section expanded
  sectionExpanded: boolean;
  // Level 2: Which issue groups are expanded (by group id)
  expandedGroups: Set<string>;
  // Level 2: Which individual anomalies are expanded (by anomaly id)
  expandedAnomalies: Set<string>;
}

/**
 * Two-Level Expandable/Collapsible Issues Detection Panel
 * 
 * Level 1: Section Header - "Detected Issues" with count
 * Level 2: Issue Cards - Consolidated groups or individual issues
 *          - Anomaly items displayed within groups (no dropdown)
 */
export function DetectedIssuesPanel({ 
  anomalies, 
  defaultExpanded = true,
  onExpansionChange 
}: DetectedIssuesPanelProps) {
  // State management for two-level expansion
  const [expansionState, setExpansionState] = useState<ExpansionState>({
    sectionExpanded: defaultExpanded,
    expandedGroups: new Set<string>(),
    expandedAnomalies: new Set<string>(),
  });

  // Determine if we should use consolidation
  const useConsolidation = useMemo(() => shouldConsolidate(anomalies), [anomalies]);
  const consolidated = useMemo(() => 
    useConsolidation ? consolidateAnomalies(anomalies) : null,
    [anomalies, useConsolidation]
  );

  // Level 1: Toggle entire section
  const toggleSection = useCallback(() => {
    setExpansionState(prev => {
      const newExpanded = !prev.sectionExpanded;
      onExpansionChange?.(newExpanded);
      return {
        ...prev,
        sectionExpanded: newExpanded,
      };
    });
  }, [onExpansionChange]);

  // Level 2: Toggle issue group
  const toggleGroup = useCallback((groupId: string) => {
    setExpansionState(prev => {
      const newExpandedGroups = new Set(prev.expandedGroups);
      if (newExpandedGroups.has(groupId)) {
        newExpandedGroups.delete(groupId);
      } else {
        newExpandedGroups.add(groupId);
      }
      return {
        ...prev,
        expandedGroups: newExpandedGroups,
      };
    });
  }, []);

  // Level 2: Toggle individual anomaly (for non-consolidated view)
  const toggleAnomaly = useCallback((anomalyId: string) => {
    setExpansionState(prev => {
      const newExpandedAnomalies = new Set(prev.expandedAnomalies);
      if (newExpandedAnomalies.has(anomalyId)) {
        newExpandedAnomalies.delete(anomalyId);
      } else {
        newExpandedAnomalies.add(anomalyId);
      }
      return {
        ...prev,
        expandedAnomalies: newExpandedAnomalies,
      };
    });
  }, []);

  // Expand/Collapse all groups
  const expandAllGroups = useCallback(() => {
    if (useConsolidation && consolidated) {
      setExpansionState(prev => ({
        ...prev,
        expandedGroups: new Set(consolidated.map(g => g.id)),
      }));
    }
  }, [useConsolidation, consolidated]);

  const collapseAllGroups = useCallback(() => {
    setExpansionState(prev => ({
      ...prev,
      expandedGroups: new Set(),
    }));
  }, []);

  // Get severity icon with tooltip
  const getSeverityIcon = (severity: AnomalySeverity) => {
    const severityLabel = severity.charAt(0).toUpperCase() + severity.slice(1);
    const icon = (() => {
      switch (severity) {
        case 'high':
          return <XCircle className="h-4 w-4 text-red-600" />;
        case 'medium':
          return <AlertTriangle className="h-4 w-4 text-orange-600" />;
        case 'low':
          return <Info className="h-4 w-4 text-blue-600" />;
      }
    })();

    return (
      <div 
        className="flex-shrink-0"
        title={`${severityLabel} Severity`}
        role="img"
        aria-label={`${severityLabel} severity issue`}
      >
        {icon}
      </div>
    );
  };

  if (anomalies.length === 0) {
    return null;
  }

  const hasExpandedGroups = expansionState.expandedGroups.size > 0;
  const allGroupsExpanded = useConsolidation && consolidated 
    ? expansionState.expandedGroups.size === consolidated.length
    : false;

  return (
    <Card className="border-0 bg-orange-50/50 dark:bg-orange-950/20 mb-4">
      {/* Level 1: Section Header */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <button
            onClick={toggleSection}
            className="flex items-center gap-2 flex-1 text-left hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded"
            aria-expanded={expansionState.sectionExpanded}
            aria-controls="detected-issues-content"
          >
            {expansionState.sectionExpanded ? (
              <ChevronDown className="h-4 w-4 text-orange-600 transition-transform duration-200" />
            ) : (
              <ChevronRight className="h-4 w-4 text-orange-600 transition-transform duration-200" />
            )}
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <CardTitle className="text-sm font-medium">
              Detected Issues
            </CardTitle>
            <Badge variant="outline" className="ml-2">
              {anomalies.length} {anomalies.length === 1 ? 'issue' : 'issues'}
            </Badge>
          </button>

          {/* Expand/Collapse All Controls (only show when section is expanded and using consolidation) */}
          {expansionState.sectionExpanded && useConsolidation && consolidated && consolidated.length > 1 && (
            <div className="flex items-center gap-2 ml-4">
              {!allGroupsExpanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={expandAllGroups}
                  className="h-7 text-xs"
                >
                  Expand All
                </Button>
              )}
              {hasExpandedGroups && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={collapseAllGroups}
                  className="h-7 text-xs"
                >
                  Collapse All
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      {/* Level 2 & 3: Issue Cards and Anomaly Details */}
      {expansionState.sectionExpanded && (
        <CardContent
          id="detected-issues-content"
          className="animate-in slide-in-from-top-2 duration-200"
        >
          <div className="space-y-3">
            {useConsolidation && consolidated ? (
              // Consolidated view - Level 2: Issue Groups
              consolidated.map((group) => (
                <IssueGroupCard
                  key={group.id}
                  group={group}
                  isExpanded={expansionState.expandedGroups.has(group.id)}
                  onToggle={() => toggleGroup(group.id)}
                  getSeverityIcon={getSeverityIcon}
                />
              ))
            ) : (
              // Individual view - Level 2: Individual Issues (no consolidation)
              anomalies.map((anomaly) => (
                <IndividualIssueCard
                  key={anomaly.id}
                  anomaly={anomaly}
                  isExpanded={expansionState.expandedAnomalies.has(anomaly.id)}
                  onToggle={() => toggleAnomaly(anomaly.id)}
                  getSeverityIcon={getSeverityIcon}
                />
              ))
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// Level 2: Issue Group Card (Consolidated)
interface IssueGroupCardProps {
  group: ConsolidatedAnomaly;
  isExpanded: boolean;
  onToggle: () => void;
  getSeverityIcon: (severity: AnomalySeverity) => React.ReactNode;
}

function IssueGroupCard({
  group,
  isExpanded,
  onToggle,
  getSeverityIcon,
}: IssueGroupCardProps) {
  return (
    <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
      {/* Level 2: Group Header (Clickable) */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-inset"
        aria-expanded={isExpanded}
        aria-controls={`group-content-${group.id}`}
      >
        {/* Expansion Icon */}
        <div className="mt-0.5 flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-600 transition-transform duration-200" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-600 transition-transform duration-200" />
          )}
        </div>

        {/* Severity Icon (with tooltip) */}
        <div className="mt-0.5">
          {getSeverityIcon(group.severity)}
        </div>

        {/* Group Summary */}
        <div className="flex-1 text-left space-y-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-semibold text-sm">{group.summary}</span>
            {group.count > 1 && (
              <Badge variant="outline" className="text-xs">
                {group.count} occurrences
              </Badge>
            )}
          </div>
          
          {/* Brief preview when collapsed */}
          {!isExpanded && group.details.length > 0 && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              {group.details[0].day && `${group.details[0].day}: `}
              {group.details[0].message}
              {group.details.length > 1 && ` and ${group.details.length - 1} more...`}
            </p>
          )}
        </div>
      </button>

      {/* Level 3: Expanded Group Content */}
      {isExpanded && (
        <div
          id={`group-content-${group.id}`}
          className="px-3 pb-3 pl-12 space-y-3 animate-in slide-in-from-top-2 duration-200"
        >
          {/* Details list */}
          <ul className="space-y-2">
            {group.details.map((detail, idx) => (
              <li key={idx} className="flex items-start gap-2 text-xs">
                <span className="text-muted-foreground mt-0.5">•</span>
                <span>
                  {detail.day && <span className="font-medium">{detail.day}: </span>}
                  {detail.message}
                </span>
              </li>
            ))}
          </ul>
          
          {/* Insight */}
          <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-2">
              <span className="text-xs">💡</span>
              <p className="text-xs text-muted-foreground flex-1">{group.insight}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Level 2: Individual Issue Card (Non-Consolidated)
interface IndividualIssueCardProps {
  anomaly: Anomaly;
  isExpanded: boolean;
  onToggle: () => void;
  getSeverityIcon: (severity: AnomalySeverity) => React.ReactNode;
}

function IndividualIssueCard({
  anomaly,
  isExpanded,
  onToggle,
  getSeverityIcon,
}: IndividualIssueCardProps) {
  const hasDetails = Boolean(anomaly.details);

  return (
    <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
      <button
        onClick={hasDetails ? onToggle : undefined}
        disabled={!hasDetails}
        className={`w-full flex items-start gap-3 p-3 text-left ${
          hasDetails 
            ? 'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-inset' 
            : 'cursor-default'
        }`}
        aria-expanded={hasDetails ? isExpanded : undefined}
        aria-controls={hasDetails ? `anomaly-content-${anomaly.id}` : undefined}
      >
        {/* Expansion Icon (only if has details) */}
        {hasDetails && (
          <div className="mt-0.5 flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600 transition-transform duration-200" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600 transition-transform duration-200" />
            )}
          </div>
        )}

        {/* Severity Icon (with tooltip) */}
        <div className="mt-0.5">
          {getSeverityIcon(anomaly.severity)}
        </div>

        {/* Issue Content */}
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-medium text-sm">{anomaly.message}</span>
          </div>
          
          {/* Brief preview when collapsed */}
          {!isExpanded && hasDetails && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              {anomaly.details}
            </p>
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && hasDetails && (
        <div
          id={`anomaly-content-${anomaly.id}`}
          className="px-3 pb-3 pl-12 animate-in slide-in-from-top-2 duration-200"
        >
          <p className="text-xs text-muted-foreground">{anomaly.details}</p>
        </div>
      )}
    </div>
  );
}


